import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import { logout } from "../actions/auth";
import { isNullUndefined } from "../util/util";
import history from "../helpers/history";
import { REFRESH_TOKEN_ALERT } from "../actions/type";

export default function PrivateRoute({ children }) {
  const isLoggedIn = useSelector(state => state.auth.isLoggedIn);
  const user = useSelector(state => state.auth.user);
  const refreshTokenAlert = useSelector(state => state.auth.refreshTokenAlert)
  const dispatch = useDispatch();
  const intervalId = useRef(null);
  const location = useLocation();
  const onListingsRoutes = location.pathname.includes("listings");

  const logoutAction = () => {
    if (intervalId.current) clearInterval(intervalId.current);
    if(isLoggedIn) {
      dispatch(logout());
    }
    history.push("/");
  };

  const handleLogoutTimer = async () => {
    // If no user or session info, logout immediately
    if (isNullUndefined(user) || !user.expiresAt) {
      logoutAction();
      return;
    }

    const now = new Date().getTime();
    const secToExpire = (Number(user.expiresAt) - now) / 1000;

    // If already refreshing, wait for that to finish
    if (refreshTokenAlert && secToExpire > 0) return;

    // If token is expired OR about to expire (within 10m)
    if (secToExpire > 0 && secToExpire < 60 * 10) {
      try {
        dispatch({
          type: REFRESH_TOKEN_ALERT,
          payload: { refreshTokenAlert: true },
        });
      } catch (error) {
        console.error("Failed to refresh token", error);
        logoutAction();
      }
    } else if (secToExpire <= 0) {
      const refreshExpiresAt = Number(user.refreshTokenExpiresAt);

      if (refreshExpiresAt <= now) {
        // Refresh token also expired, logout
        logoutAction();
      }
    }
  };

  useEffect(() => {
    if (isLoggedIn && user) {
      // Check every 5 seconds
      intervalId.current = setInterval(handleLogoutTimer, 5000);
      // Run once immediately on mount
      handleLogoutTimer();
    }

    return () => {
      if (intervalId.current) clearInterval(intervalId.current);
    };
  }, [isLoggedIn, user]);

  if (!isLoggedIn && onListingsRoutes) {
    if (
      location.pathname !== "/listings" &&
      location.pathname !== "/listings/explore"
    ) {
      return <Navigate to="/listings/signIn" replace />;
    }
  } else if (!isLoggedIn && !onListingsRoutes) {
    return <Navigate to="/" replace />;
  }

  return children;
}
